import React, { FC } from 'react'

import { Button, Stack, Typography, useTheme } from '@mui/material'
import Box from '@mui/material/Box'
import { format } from 'date-fns'
import { de, enUS, es, fr, it, uk } from 'date-fns/locale'
import { isNull } from 'lodash'
import { useTranslation } from 'react-i18next'

import { ReactComponent as ArrowRightIcon } from '../../../assets/icons/arrow-right.svg'
import { useAppSelector } from '../../../store/hooks'
import { currencyFormatter } from '../../../utils'
import { DailyStatsHeaderProps } from '../types'

import { DailyStatItem } from './DailyStatInfo'

export const DailyStatsCardHead: FC<DailyStatsHeaderProps> = ({
  date,
  opened,
  dailyStatsInfo,
  handleSetOpened,
  isForModal,
}) => {
  const {
    totalTrades,
    winners,
    losers,
    winRatio,
    profitFactor,
    grossProfit,
    brokerCommissionsTotal,
    commissionsTotal,
    swapTotal,
  } = dailyStatsInfo
  const { currentAccount } = useAppSelector(({ userAccounts }) => userAccounts)
  const { spacing } = useTheme()
  const { t, i18n } = useTranslation()
  const language = (i18n.resolvedLanguage || i18n.language || 'en').split('-')[0]
  const dateLocale = { en: enUS, uk, es, fr, it, de }[language] || enUS
  const formattedDate = format(new Date(date), 'eeee, dd MMMM yyyy', { locale: dateLocale })
  const capitalizedFormattedDate = formattedDate.charAt(0).toUpperCase() + formattedDate.slice(1)

  // comissions can be negative or positive so to calc netPnL we add it
  const netPnL = grossProfit + commissionsTotal

  return (
    <Box display={'flex'} width={'100%'} px={2}>
      {!isForModal && (
        <Box mt={spacing(0.5)}>
          <Button
            onClick={handleSetOpened}
            disableRipple
            sx={{
              minWidth: 'auto',
              padding: 0,
              '&:hover': {
                background: 'transparent',
              },
            }}
          >
            <ArrowRightIcon
              style={{
                transform: opened ? 'rotate(90deg)' : 'none',
                transition: '0.25s transform',
              }}
            />
          </Button>
        </Box>
      )}

      <Box p={spacing(0, 2.5, 2, 2)}>
        <Typography variant="h4" fontSize={{ xs: 16, md: 20 }} mb={spacing(4.75)}>
          {capitalizedFormattedDate}
        </Typography>

        <Stack
          direction={'row'}
          gap={3}
          alignItems={'center'}
          justifyContent={'flex-start'}
          flexWrap={'wrap'}
        >
          <DailyStatItem
            title={t('dailyStatsPage.stats.totalTrades', 'Total trades')}
            description={totalTrades}
          />
          <DailyStatItem
            title={t('dailyStatsPage.stats.winners', 'Winners')}
            description={winners}
          />
          <DailyStatItem title={t('dailyStatsPage.stats.losers', 'Losers')} description={losers} />
          <DailyStatItem
            title={t('dailyStatsPage.stats.winRatio', 'Win ratio')}
            description={!isNaN(winRatio) && !isNull(winRatio) ? `${winRatio.toFixed(1)}%` : '-'}
          />
          <DailyStatItem
            title={t('dailyStatsPage.stats.profitFactor', 'Profit factor')}
            description={
              !isNaN(profitFactor) && !isNull(profitFactor) ? profitFactor.toFixed(2) : '-'
            }
          />
          <DailyStatItem
            title={t('dailyStatsPage.stats.pnl', 'PnL')}
            description={
              Boolean(grossProfit)
                ? currencyFormatter(currentAccount?.currency, Number(grossProfit.toFixed(2)))
                : '-'
            }
          />
          <DailyStatItem
            title={t('dailyStatsPage.stats.netPnl', 'Net PnL')}
            description={
              Boolean(netPnL)
                ? currencyFormatter(currentAccount?.currency, Number(netPnL.toFixed(2)))
                : '-'
            }
          />
          <DailyStatItem
            title={t('dailyStatsPage.stats.commissions', 'Commissions')}
            description={
              Boolean(brokerCommissionsTotal)
                ? currencyFormatter(
                    currentAccount?.currency,
                    Number(brokerCommissionsTotal.toFixed(2)),
                  )
                : '-'
            }
          />
          <DailyStatItem
            title={t('dailyStatsPage.stats.swap', 'Swap')}
            description={
              Boolean(swapTotal)
                ? currencyFormatter(currentAccount?.currency, Number(swapTotal.toFixed(2)))
                : '-'
            }
          />
        </Stack>
      </Box>
    </Box>
  )
}
